import { useEffect, useMemo, useState } from "react";
import {
  Container,
  TextField,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Stack,
  Typography,
  Box,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useAuth } from "./auth";

const OFFICER_LAST_USED_KEY = "patternGenerator.lastOfficer.v1";
const IO_DESIGNATION_MAP = {
  AD: "Assistant Director",
  IP: "Inspector",
  SI: "Sub-Inspector",
};

const normalizeOfficerName = (value) => String(value || "").trim().replace(/\s+/g, " ");
const normalizeIoDesignation = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const normalized = IO_DESIGNATION_MAP[raw.toUpperCase()];
  if (normalized) return normalized;
  if (Object.values(IO_DESIGNATION_MAP).includes(raw)) return raw;
  return "";
};

const formatOfficerDisplay = (name, designation) => {
  const normalizedName = normalizeOfficerName(name);
  const normalizedDesignation = normalizeIoDesignation(designation);
  if (!normalizedName) return "";
  if (!normalizedDesignation) return normalizedName;
  return `${normalizedName} & ${normalizedDesignation.toUpperCase()}`;
};

const readLastOfficer = () => {
  if (typeof window === "undefined") return "";
  return normalizeOfficerName(window.localStorage.getItem(OFFICER_LAST_USED_KEY) || "");
};

const operators = [
  "Telenor",
  "Ufone",
  "Jazz/Warid",
  "Zong",
  "SCO",
  "Onic",
  "Rox",
];

const PatternGeneratorView = () => {
  const { user, isAuthenticated } = useAuth();
  const profileOfficerDisplay = useMemo(
    () =>
      isAuthenticated
        ? formatOfficerDisplay(
            user?.investigationOfficerName || user?.name || "",
            user?.ioDesignation || user?.rank || ""
          )
        : "",
    [isAuthenticated, user?.investigationOfficerName, user?.ioDesignation, user?.name, user?.rank]
  );
  const initialOfficer = useMemo(
    () => readLastOfficer() || profileOfficerDisplay || "",
    [profileOfficerDisplay]
  );
  const [appliedProfileOfficer, setAppliedProfileOfficer] = useState(false);
  const [formData, setFormData] = useState({
    enquiryCase: "FIR/", // Enforce "FIR/" prefix
    phoneNumber: "",
    priority: "",
    operator: operators[0], // Default operator
    officer: initialOfficer,
    type: "CDR",
    imeiNumber: "",
    startDate: null,
    endDate: null,
    pattern: "",
  });

  const [error, setError] = useState("");

  useEffect(() => {
    if (appliedProfileOfficer || !profileOfficerDisplay || readLastOfficer()) return;
    setFormData((prev) => ({ ...prev, officer: profileOfficerDisplay }));
    setAppliedProfileOfficer(true);
  }, [appliedProfileOfficer, profileOfficerDisplay]);

  const rememberOfficer = (rawOfficer) => {
    const officer = normalizeOfficerName(rawOfficer);
    if (!officer || typeof window === "undefined") return;
    window.localStorage.setItem(OFFICER_LAST_USED_KEY, officer);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Ensure "FIR/" prefix for Enquiry/Case field
    if (name === "enquiryCase" && !value.startsWith("FIR/")) {
      setError("Enquiry/Case Number must start with 'FIR/'.");
      return;
    }

    setFormData((prev) => {
      const updatedFormData = { ...prev, [name]: value };
      generatePattern(updatedFormData);
      return updatedFormData;
    });
    setError("");
  };

  const handleDateChange = (field, date) => {
    setFormData((prev) => {
      const updatedFormData = { ...prev, [field]: date };
      generatePattern(updatedFormData);
      return updatedFormData;
    });
  };

  const generatePattern = (updatedFormData = formData) => {
    const {
      enquiryCase,
      phoneNumber,
      operator,
      officer,
      priority,
      type,
      imeiNumber,
      startDate,
      endDate,
    } = updatedFormData;

    // Validation
    if (!enquiryCase.startsWith("FIR/")) {
      setError("Enquiry/Case Number must contain 'FIR/'.");
      return;
    }

    if (type === "CDR" && (!phoneNumber || !/^923\d{9}$/.test(phoneNumber))) {
      setError("Phone number must start with 923 and be exactly 12 digits.");
      return;
    }

    if (type === "IMEI" && (!imeiNumber || imeiNumber.length < 14 || imeiNumber.length > 15)) {
      setError("IMEI number must be 14 or 15 digits.");
      return;
    }

    if (!startDate || !endDate) {
      setError("Start and End dates are required.");
      return;
    }

    if (dayjs(startDate).isAfter(dayjs(endDate))) {
      setError("End date must be after Start date.");
      return;
    }

    setError(""); // Clear errors

    const formattedStartDate = dayjs(startDate).format("DD-MM-YYYY");
    const formattedEndDate = dayjs(endDate).format("DD-MM-YYYY");

    let pattern = "";
    if (type === "CDR") {
      switch (operator) {
        case "Telenor":
          pattern = `tpn:${phoneNumber}:${formattedStartDate}:${formattedEndDate}:`;
          break;
        case "Jazz/Warid":
        case "Zong":
        case "SCO":
        case "Onic":
        case "Rox":
          pattern = `A;${phoneNumber};${dayjs(startDate).format("DD/MM/YYYY")};${dayjs(endDate).format(
            "DD/MM/YYYY"
          )};`;
          break;
        case "Ufone":
          pattern = `MSISDN|Both|${dayjs(startDate).format("MM/DD/YYYY")}|${dayjs(endDate).format(
            "MM/DD/YYYY"
          )}|${phoneNumber}`;
          break;
        default:
          pattern = "Unsupported operator for CDR.";
      }
    } else if (type === "IMEI") {
      switch (operator) {
        case "Telenor":
          const imei14 = imeiNumber.slice(0, 14);
          pattern = `tpi:${imeiNumber},${imei14}:${formattedStartDate}:${formattedEndDate}:`;
          break;
        case "Jazz/Warid":
          pattern = `I;${imeiNumber};${dayjs(startDate).format("DD/MM/YYYY")};${dayjs(endDate).format(
            "DD/MM/YYYY"
          )};`;
          break;
        case "Ufone":
          pattern = `IMEI|Both|${dayjs(startDate).format("MM/DD/YYYY")}|${dayjs(endDate).format(
            "MM/DD/YYYY"
          )}|${imeiNumber}`;
          break;
        default:
          pattern = "Unsupported operator for IMEI.";
      }
    }

    rememberOfficer(officer);

    setFormData({
      ...updatedFormData,
      pattern: `${priority ? `${priority} ` : ""}Please provide the ${
        type === "CDR" ? "CDR (Call+Sms+Data)" : "CDR by IMEI"
      } of following (${operator}) in ${enquiryCase} (${officer}):\n${pattern}`,
    });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container maxWidth="lg">
        <Typography variant="h4" gutterBottom>
          Pattern Generator
        </Typography>
        <Stack spacing={3} direction={{ xs: "column", md: "row" }}>
          <Box flex={1} sx={{ width: { xs: "100%", md: "50%" } }}>
            <Stack spacing={3}>
              <TextField
                label="Enquiry/Case Number"
                name="enquiryCase"
                value={formData.enquiryCase}
                onChange={(e) => {
                  const value = e.target.value;
                  if (!value.startsWith("FIR/")) {
                    setError("Enquiry/Case Number must start with 'FIR/'.");
                    return;
                  }
                  setFormData({ ...formData, enquiryCase: value });
                  setError("");
                }}
                fullWidth
                helperText="Enquiry/Case Number must start with 'FIR/'."
                error={!!error}
              />
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  name="type"
                  value={formData.type}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData({ ...formData, type: value });

                    // Reset phoneNumber or imeiNumber when switching types
                    if (value === "CDR") {
                      setFormData((prev) => ({ ...prev, imeiNumber: "" }));
                    } else if (value === "IMEI") {
                      setFormData((prev) => ({ ...prev, phoneNumber: "" }));
                    }
                  }}
                >
                  <MenuItem value="CDR">CDR</MenuItem>
                  <MenuItem value="IMEI">IMEI</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Operator</InputLabel>
                <Select
                  name="operator"
                  value={formData.operator}
                  onChange={handleChange}
                >
                  {operators.map((operator) => (
                    <MenuItem key={operator} value={operator}>
                      {operator}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {formData.type === "CDR" && (
                <TextField
                  label="Phone Number"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (!/^\d*$/.test(value)) {
                      setError("Phone number must contain only digits.");
                      return;
                    }
                    if (value && !/^923\d{9}$/.test(value)) {
                      setError("Phone number must start with 923 and be exactly 12 digits.");
                    } else {
                      setError("");
                    }
                    setFormData({ ...formData, phoneNumber: value });
                  }}
                  fullWidth
                  helperText="Must start with 923 and be 12 digits."
                  error={!!error && formData.phoneNumber.length > 0}
                />
              )}
              {formData.type === "IMEI" && (
                <TextField
                  label="IMEI Number"
                  name="imeiNumber"
                  value={formData.imeiNumber}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (!/^\d*$/.test(value)) {
                      setError("IMEI number must contain only digits.");
                      return;
                    }
                    setFormData({ ...formData, imeiNumber: value });
                    if (value.length > 0 && (value.length < 14 || value.length > 15)) {
                      setError("IMEI number must be 14 or 15 digits.");
                    } else {
                      setError("");
                    }
                  }}
                  fullWidth
                  helperText="IMEI number must be 14 or 15 digits."
                  error={!!error && formData.imeiNumber.length > 0}
                />
              )}
              <TextField
                label="Investigation Officer"
                name="officer"
                value={formData.officer}
                onChange={(e) =>
                  handleChange({
                    target: { name: "officer", value: normalizeOfficerName(e.target.value) },
                  })
                }
                onBlur={() => rememberOfficer(formData.officer)}
                placeholder="Type officer name"
                helperText="Editable field. Saved in your browser and auto-filled next time."
                fullWidth
              />
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  name="priority"
                  value={formData.priority}
                  onChange={handleChange}
                >
                  <MenuItem value="">Normal</MenuItem>
                  <MenuItem value="Most Urgent">Most Urgent</MenuItem>
                </Select>
              </FormControl>
              <DatePicker
                label="Start Date"
                value={formData.startDate}
                onChange={(date) => handleDateChange("startDate", date)}
                minDate={dayjs().subtract(364, "days")}
                maxDate={dayjs()}
                disableFuture
                fullWidth
              />
              <DatePicker
                label="End Date"
                value={formData.endDate}
                onChange={(date) => handleDateChange("endDate", date)}
                minDate={formData.startDate}
                maxDate={dayjs()}
                fullWidth
                disabled={!formData.startDate}
              />
            </Stack>
          </Box>
          <Box flex={1} sx={{ width: { xs: "100%", md: "50%" } }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => generatePattern()}
              sx={{ mb: 3, width: { xs: "100%", md: "auto" } }}
            >
              Generate Pattern
            </Button>
            <TextField
              label="Generated Pattern"
              value={formData.pattern}
              fullWidth
              multiline
              rows={12}
              InputProps={{ readOnly: true }}
            />
          </Box>
        </Stack>
        {error && <Typography color="error">{error}</Typography>}
      </Container>
    </LocalizationProvider>
  );
};

export default PatternGeneratorView;
